import React, { useId } from "react";
import { ToggleSwitchProps } from "./types";

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label,
  description,
  isChecked,
  isDisabled = false,
  handleToggle,
  id,
  containerClassName,
  labelClassName,
  descriptionClassName,
  trackerClassName,
  thumbClassName,
  checkedTranslateClassName = "translate-x-4.5",
  uncheckedTranslateClassName = "translate-x-0.5",
  checkedIcon,
  uncheckedIcon,
}) => {
  const generatedId = useId();
  const switchId = id || generatedId;
  const descriptionId = description ? `${switchId}-desc` : undefined;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleToggle?.();
    }
  };

  return (
    <div className={`flex items-center gap-3 ${containerClassName}`}>
      {(label || description) && (
        <div className="flex flex-col">
          {label && (
            <label htmlFor={switchId} className={`${labelClassName}`}>
              {label}
            </label>
          )}
          {description && (
            <span id={descriptionId} className={`${descriptionClassName}`}>
              {description}
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        id={switchId}
        role="switch"
        aria-checked={isChecked}
        aria-disabled={isDisabled}
        aria-describedby={descriptionId}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        tabIndex={0}
        className={`relative inline-flex h-5 w-9 items-center rounded-full focus:outline-none transition-colors duration-200 ${
          isChecked
            ? "bg-verba-blue-dark-mode dark:bg-verba-blue-dark-mode-dk"
            : "bg-gray-175 dark:bg-gray-175-dk"
        } ${isDisabled ? "cursor-not-allowed opacity-60" : ""} ${trackerClassName}`}
      >
        <span
          className={`inline-flex items-center justify-center h-4 w-4 shadow-sm-blur transform rounded-full bg-pure-white dark:bg-pure-white-dk transition-transform duration-200 ${
            isChecked ? checkedTranslateClassName : uncheckedTranslateClassName
          } ${thumbClassName}`}
        >
          {isChecked ? checkedIcon : uncheckedIcon}
        </span>
      </button>
    </div>
  );
};

export default ToggleSwitch;
